import React from 'react'

import { ButtonColorVariant, ButtonVariant } from '@gousto-internal/citrus-react'

import type { StandardRecipeItem } from '@library/api-menu-experience'
import type { RecipeUUID } from '@library/menu-service'

import { ActionButton, FloatingButtonContainer } from '../RecipeTile.styles'

type BasketButtonProps = {
  recipe: StandardRecipeItem
  isOutOfStock: boolean
  isInBasket: boolean
  canAddRecipes: boolean
  reachedLimit: boolean
  isDisabled?: boolean
  onAddClick: (recipeId: RecipeUUID) => void
  onRemoveClick: (recipeId: RecipeUUID) => void
  onBlockedAddAttempt: () => void
}

export const BasketButton = ({
  recipe,
  isInBasket,
  isOutOfStock,
  canAddRecipes,
  reachedLimit,
  isDisabled = false,
  onAddClick,
  onRemoveClick,
  onBlockedAddAttempt,
}: BasketButtonProps) => {
  const atLimit = reachedLimit && !isInBasket
  const disabled = isDisabled || (!isInBasket && (isOutOfStock || atLimit))
  const ariaLabel = isInBasket ? `Remove ${recipe.title}` : `Add ${recipe.title}`
  const dataTesting = disabled
    ? 'menuRecipeAddDisabled'
    : isInBasket
    ? 'menuRecipeCardRemove'
    : 'menuRecipeAdd'

  const handleClick = () => {
    if (disabled) {
      return
    }

    if (isInBasket) {
      onRemoveClick(recipe.id)
      return
    }

    if (!canAddRecipes) {
      onBlockedAddAttempt()
      return
    }

    onAddClick(recipe.id)
  }

  return (
    <FloatingButtonContainer>
      <ActionButton
        variant={ButtonVariant.Icon}
        icon={isInBasket ? 'remove' : 'add'}
        colorVariant={isInBasket ? ButtonColorVariant.Secondary : ButtonColorVariant.Primary}
        disabled={disabled}
        onClick={handleClick}
        data-testid="recipe-tile-basket-button"
        data-testing={dataTesting}
        aria-label={ariaLabel}
      />
    </FloatingButtonContainer>
  )
}
